/**
 * Concrete onboarding preview — a composed 9:16 frame that shows what a
 * finished clip will look like in the brand being configured: the uploaded
 * logo (at its real position/size/opacity), a speaker name card, and a caption
 * (two-line gold punch or uniform) in the chosen colors AND the uploaded font,
 * over a fixed, neutral 9:16 sample frame that is the same for every brand so
 * the layout reads clearly on a real podcast still (never the operator's own
 * reference screenshot — that is saved as a style reference, not shown here).
 *
 * Geometry is faithful to the real 2160×3840 render canvas: every size/offset
 * is expressed in CSS container-query units (cqw/cqh) against a `container-type`
 * frame, so `pctH(px)` = px as a share of canvas height scales exactly at any
 * preview width.
 */
import previewSampleUrl from "../assets/preview-sample.jpg";

const CANVAS_W = 2160;
const CANVAS_H = 3840;

export interface ChannelPreviewProps {
  captionColor: string;
  captionAccent: string;
  speakerNameColor: string;
  watermark?: string;
  logoDataUrl: string | null;
  logoWidthPct: number;
  logoBottomPx: number;
  logoOpacity: number;
  /** Horizontal centre of the logo as % of canvas width. Absent → 50% (founding). */
  logoXPct?: number;
  /** Free x/y placement of the speaker card (% of canvas, top-left anchor) +
   *  optional size multiplier. Absent → founding layout (byte-identical). */
  speakerCard?: { xPct: number; yPct: number; scale?: number } | null;
  capBottomPx: number;
  capFontSizePx: number;
  /** Two-line mode: independent top & punch line sizes. Absent → derived from
   *  capFontSizePx (byte-identical to the old 0.95×/1.9× behaviour). */
  capTopPx?: number;
  capPunchPx?: number;
  capUniform: boolean;
  capBox: boolean;
  capBoxOpacity: number;
  capFlat: boolean;
  capSidePaddingPx: number;
  capLineHeight: number;
  /** Uploaded font: injected via @font-face so the preview uses the real face. */
  fontFamily?: string;
  fontDataUrl?: string | null;
  /** Per-element font families (registered by the parent via @font-face).
   *  Absent → the single fontFamily / inherit, byte-identical to before. */
  capFontFamily?: string;
  punchFontFamily?: string;
  speakerFontFamily?: string;
  /** Caption text direction — RTL for Arabic/Hebrew, LTR for French/Latin. */
  direction?: "rtl" | "ltr";
  /** Sample caption to render in the channel's language (top line + bottom punch). */
  sampleTop?: string;
  samplePunch?: string;
  /** Sample speaker name + role in the channel's language. */
  sampleSpeaker?: { name: string; role: string };
}

export function ChannelPreview(props: ChannelPreviewProps): React.JSX.Element {
  const {
    captionColor, captionAccent, speakerNameColor, watermark,
    logoDataUrl, logoWidthPct, logoBottomPx, logoOpacity, logoXPct, speakerCard,
    capBottomPx, capFontSizePx, capTopPx, capPunchPx, capUniform, capBox, capBoxOpacity, capFlat, capSidePaddingPx, capLineHeight,
    fontFamily, fontDataUrl,
    capFontFamily, punchFontFamily, speakerFontFamily,
    direction = "rtl",
    sampleTop = "لن تحقق شيئًا إن لم", samplePunch = "تطلب ما تريد",
    sampleSpeaker = { name: "نولان جوكوفيتش", role: "لاعب تنس محترف" },
  } = props;
  const isRtl = direction === "rtl";

  const useFont = fontFamily && fontDataUrl;
  const fontStack = useFont ? `"${fontFamily}", "Segoe UI", sans-serif` : "inherit";
  const pctH = (px: number): string => `${(px / CANVAS_H) * 100}cqh`;
  const pctW = (px: number): string => `${(px / CANVAS_W) * 100}cqw`;
  const shadow = capFlat ? "none" : "0 0.3cqh 0.9cqh rgba(0,0,0,.85)";
  const topPx = capTopPx ?? Math.round(capFontSizePx * 0.95);
  const bottomPx = capPunchPx ?? Math.round(capFontSizePx * 1.9);
  const roleStack = (fam?: string): string | undefined => (fam ? `"${fam}", "Segoe UI", sans-serif` : undefined);

  const captionBlock: React.CSSProperties = {
    position: "absolute",
    bottom: pctH(capBottomPx),
    left: pctW(capSidePaddingPx),
    right: pctW(capSidePaddingPx),
    textAlign: "center",
    lineHeight: capLineHeight,
    ...(capBox
      ? { background: `rgba(0,0,0,${capBoxOpacity})`, borderRadius: "1.2cqh", padding: "0.8cqh 2cqw" }
      : {}),
  };

  return (
    <>
      {useFont && (
        <style>{`@font-face { font-family: "${fontFamily}"; src: url(${fontDataUrl}); font-display: swap; }`}</style>
      )}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
          containerType: "size",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,.1)",
          background: `#000 center / cover no-repeat url(${previewSampleUrl})`,
          fontFamily: fontStack,
        }}
      >
        {/* Legibility scrim (heavier at the bottom where captions sit) */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.6), rgba(0,0,0,.05) 45%, rgba(0,0,0,.25))" }} />

        {/* Speaker name card mock — free x/y (top-left anchor) when a
            speakerCard position is supplied; else the founding reading-start
            side layout (unchanged for callers that don't pass one). */}
        <div
          dir={direction}
          style={{
            position: "absolute",
            ...(speakerCard
              ? { top: `${speakerCard.yPct}cqh`, left: `${speakerCard.xPct}cqw` }
              : { top: pctH(360), [isRtl ? "right" : "left"]: pctW(120) }),
            textAlign: isRtl ? "right" : "left",
          }}
        >
          <div style={{ color: speakerNameColor, fontWeight: 700, fontSize: pctH(150 * (speakerCard?.scale ?? 1)), textShadow: shadow, fontFamily: roleStack(speakerFontFamily) }}>{sampleSpeaker.name}</div>
          <div style={{ color: captionColor, fontSize: pctH(78 * (speakerCard?.scale ?? 1)), opacity: 0.9, textShadow: shadow, marginTop: "0.4cqh", fontFamily: roleStack(speakerFontFamily) }}>{sampleSpeaker.role}</div>
        </div>

        {/* Logo */}
        {logoDataUrl && (
          <img
            src={logoDataUrl}
            alt="logo"
            style={{
              position: "absolute",
              bottom: pctH(logoBottomPx),
              left: logoXPct === undefined ? "50%" : `${logoXPct}cqw`,
              transform: "translateX(-50%)",
              width: `${logoWidthPct}cqw`,
              opacity: logoOpacity,
              objectFit: "contain",
            }}
          />
        )}

        {/* Caption */}
        <div dir={direction} style={captionBlock}>
          {capUniform ? (
            <div style={{ color: captionColor, fontWeight: 700, fontSize: pctH(capFontSizePx), textShadow: shadow, fontFamily: roleStack(capFontFamily) }}>
              {sampleTop} {samplePunch}
            </div>
          ) : (
            <>
              <div style={{ color: captionColor, fontWeight: 700, fontSize: pctH(topPx), textShadow: shadow, fontFamily: roleStack(capFontFamily) }}>{sampleTop}</div>
              <div style={{ color: captionAccent, fontWeight: 700, fontSize: pctH(bottomPx), textShadow: shadow, fontFamily: roleStack(punchFontFamily) }}>{samplePunch}</div>
            </>
          )}
        </div>

        {/* Watermark */}
        {watermark && (
          <div style={{ position: "absolute", bottom: pctH(90), left: "50%", transform: "translateX(-50%)", color: captionColor, opacity: 0.7, fontSize: pctH(58), textShadow: shadow }}>
            {watermark}
          </div>
        )}
      </div>
    </>
  );
}
